// Place signature fields on an uploaded PDF — the piece that lets any file
// (an LON, anything under "Other") go out for signature. The page is drawn
// with pdf.js; fields sit in an overlay as fractions of the page (x, y, w, h
// in 0..1, y from the top) so they are independent of zoom and land in the
// same place when gappOverlay draws them into the real PDF.
//
// Click a tool, click the page to drop a field; drag to move; drag the right
// edge to resize; × removes it. Each field belongs to a party — that is who
// signs it.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

export const FIELD_KINDS = [
  { kind: 'sig',      label: 'Signature',    w: 0.30, h: 0.045 },
  { kind: 'date',     label: 'Date signed',  w: 0.16, h: 0.030 },
  { kind: 'name',     label: 'Printed name', w: 0.30, h: 0.030 },
  { kind: 'initials', label: 'Initials',     w: 0.08, h: 0.035 },
  { kind: 'check',    label: 'Checkbox',     w: 0.022, h: 0.017 },
  // One box of a question the signer answers (the LON's agree / disagree).
  // Placed from the letter by lib/documents/lonLayout, not from the palette.
  { kind: 'choice',   label: 'Answer',       w: 0.016, h: 0.013, auto: true },
];
const kindOf = (k) => FIELD_KINDS.find((x) => x.kind === k) || FIELD_KINDS[0];

export default function FieldPlacer({ bytes, fields = [], onChange, parties, defaultParty }) {
  const [doc, setDoc] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [tool, setTool] = useState('sig');
  const [party, setParty] = useState(defaultParty || parties?.[0]?.party_role || 'client');
  const [err, setErr] = useState('');
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const drag = useRef(null);

  // load
  useEffect(() => {
    let cancelled = false;
    setDoc(null); setErr('');
    if (!bytes) return undefined;
    pdfjs.getDocument({ data: bytes.slice(0) }).promise
      .then((d) => { if (!cancelled) setDoc(d); })
      .catch((e) => { if (!cancelled) setErr(e.message || 'Could not open the PDF'); });
    return () => { cancelled = true; };
  }, [bytes]);

  // draw the current page to fit the available width
  const render = useCallback(async () => {
    if (!doc || !canvasRef.current) return;
    const page = await doc.getPage(pageNo);
    const box = canvasRef.current.parentElement.getBoundingClientRect();
    const base = page.getViewport({ scale: 1 });
    const scale = Math.max(0.5, Math.min(2, (box.width - 2) / base.width));
    const vp = page.getViewport({ scale });
    const canvas = canvasRef.current;
    canvas.width = vp.width; canvas.height = vp.height;
    canvas.style.width = `${vp.width}px`; canvas.style.height = `${vp.height}px`;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    setSize({ w: vp.width, h: vp.height });
  }, [doc, pageNo]);
  useEffect(() => { render(); }, [render]);
  useEffect(() => {
    const onResize = () => render();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [render]);

  const pageFields = fields.map((f, i) => ({ ...f, _i: i })).filter((f) => (f.page || 0) === pageNo - 1);

  const addAt = (e) => {
    if (!size.w || drag.current) return;
    const r = overlayRef.current.getBoundingClientRect();
    const k = kindOf(tool);
    const x = Math.min(Math.max((e.clientX - r.left) / size.w - k.w / 2, 0), 1 - k.w);
    const y = Math.min(Math.max((e.clientY - r.top) / size.h - k.h / 2, 0), 1 - k.h);
    onChange([...fields, { id: `f${Date.now()}`, kind: tool, party, page: pageNo - 1, x, y, w: k.w, h: k.h }]);
  };

  const startDrag = (e, idx, mode) => {
    e.stopPropagation(); e.preventDefault();
    const f = fields[idx];
    drag.current = { idx, mode, sx: e.clientX, sy: e.clientY, ox: f.x, oy: f.y, ow: f.w };
    const move = (ev) => {
      const d = drag.current; if (!d) return;
      const dx = (ev.clientX - d.sx) / size.w, dy = (ev.clientY - d.sy) / size.h;
      const cur = fields[d.idx];
      const next = d.mode === 'resize'
        ? { ...cur, w: Math.min(Math.max(d.ow + dx, 0.03), 1 - cur.x) }
        : { ...cur, x: Math.min(Math.max(d.ox + dx, 0), 1 - cur.w), y: Math.min(Math.max(d.oy + dy, 0), 1 - cur.h) };
      onChange(fields.map((x, i) => (i === d.idx ? next : x)));
    };
    const up = () => { drag.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };

  const remove = (idx) => onChange(fields.filter((_, i) => i !== idx));
  const partyLabel = (p) => (parties || []).find((x) => x.party_role === p)?.label || p;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, minHeight: 420 }}>
      <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>Click a field type, then click the page. Drag to move, drag the right edge to resize.</div>
        {FIELD_KINDS.filter((k) => !k.auto).map((k) => (
          <button key={k.kind} type="button" onClick={() => setTool(k.kind)}
            style={{ textAlign: 'left', font: 'inherit', fontSize: 13, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${tool === k.kind ? 'var(--accent)' : 'var(--border2)'}`, background: tool === k.kind ? 'var(--accent-bg)' : 'var(--card-bg)', color: 'var(--text)' }}>
            {k.label}
          </button>
        ))}
        <label style={{ fontSize: 12, color: 'var(--text3)', display: 'grid', gap: 4, marginTop: 6 }}>Signer
          <select className="form-select" value={party} onChange={(e) => setParty(e.target.value)} style={{ fontSize: 12.5 }}>
            {(parties || []).map((p) => <option key={p.party_role} value={p.party_role}>{p.label}</option>)}
          </select>
        </label>
        {doc && doc.numPages > 1 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn btn-ghost btn-sm" disabled={pageNo <= 1} onClick={() => setPageNo((n) => n - 1)}>‹</button>
            Page {pageNo} of {doc.numPages}
            <button type="button" className="btn btn-ghost btn-sm" disabled={pageNo >= doc.numPages} onClick={() => setPageNo((n) => n + 1)}>›</button>
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{fields.length} field{fields.length === 1 ? '' : 's'} placed{fields.length ? ` · ${[...new Set(fields.map((f) => f.party))].map(partyLabel).join(', ')}` : ''}</div>
      </div>

      <div style={{ position: 'relative', border: '1px solid var(--border2)', borderRadius: 6, background: '#888', overflow: 'auto', padding: 1 }}>
        {err && <div style={{ padding: 12, color: 'var(--danger)', background: '#fff' }}>{err}</div>}
        {!doc && !err && <div style={{ padding: 12, color: '#fff' }}>Opening the PDF…</div>}
        <div style={{ position: 'relative', width: size.w || 'auto', margin: '0 auto' }}>
          <canvas ref={canvasRef} style={{ display: 'block', background: '#fff' }} />
          <div ref={overlayRef} onClick={addAt} style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}>
            {pageFields.map((f) => (
              <div key={f.id || f._i} onMouseDown={(e) => startDrag(e, f._i, 'move')} onClick={(e) => e.stopPropagation()}
                title={f.kind === 'choice' ? `Ticked if they ${f.value}` : `${kindOf(f.kind).label} · ${partyLabel(f.party)}`}
                style={{ position: 'absolute', left: f.x * size.w, top: f.y * size.h, width: f.w * size.w, height: f.h * size.h,
                  border: '2px dashed var(--accent)', background: 'rgba(26,58,107,0.12)', borderRadius: 3, cursor: 'move', fontSize: 10, fontWeight: 700,
                  color: 'var(--accent)', padding: '1px 4px', overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none', boxSizing: 'border-box' }}>
                {f.kind === 'choice' ? '✓' : kindOf(f.kind).label}{!['check', 'choice'].includes(f.kind) && <span style={{ fontWeight: 400 }}> · {partyLabel(f.party)}</span>}
                <span onMouseDown={(e) => startDrag(e, f._i, 'resize')} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' }} />
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(f._i); }} onMouseDown={(e) => e.stopPropagation()}
                  style={{ position: 'absolute', right: -8, top: -9, width: 16, height: 16, borderRadius: 8, border: 'none', background: 'var(--danger)', color: '#fff', fontSize: 10, lineHeight: '16px', padding: 0, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
